import './app/grid-features/ag-grid.license';
import {
  enableProdMode,
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import {
  NgxMatDatetimePickerModule,
  NgxMatNativeDateModule,
} from '@salesware/angular-material-components-datetime-picker';
import { MatDialogModule } from '@angular/material/dialog';
import { provideNgxWebstorage, withLocalStorage } from 'ngx-webstorage';
import { ThemeModule } from './app/@theme/theme.module';
import { NbAuthModule } from '@nebular/auth';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import {
  NbDatepickerModule,
  NbDialogModule,
  NbLayoutModule,
  NbMenuModule,
  NbSidebarModule,
  NbThemeModule,
  NbToastrModule,
  NbWindowModule,
} from '@nebular/theme';
import { AngularFireModule } from '@angular/fire/compat';
import { Auth, connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app/app-routing.module';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { USE_EMULATOR as USE_FUNCTIONS_EMULATOR } from '@angular/fire/compat/functions';
import { USE_EMULATOR as USE_FIRESTORE_EMULATOR } from '@angular/fire/compat/firestore';
import { USE_EMULATOR as USE_STORAGE_EMULATOR } from '@angular/fire/compat/storage';
import { MatNativeDateModule } from '@angular/material/core';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { MessageService } from 'primeng/api';
import { provideInjectionContextAngularFirestore } from '@frontend/services/injection-context-angular-firestore';
import { providePrimeNgTheme } from '@frontend/services/primeng-theme';
import { provideCdkOverlaysBelowBodyPopups } from '@frontend/services/cdk-overlay-config';
import { provideDialogEscapeGuard } from '@frontend/services/dialog-escape-guard';
import { AuthProvider, ENABLED_AUTH_PROVIDERS } from './app/auth/auth-providers';
import { E2E_MODE } from '@frontend/auth/e2e-mode.token';

if (environment.production) {
  enableProdMode();
}

// Kept apart from the dev emulators (8080/5001) so E2E never touches imported dev data.
const emulator = environment.e2e
  ? {
      host: '127.0.0.1',
      firestorePort: 8185,
      functionsPort: 5185,
      authPort: 9185,
      storagePort: 9285,
    }
  : { host: 'localhost', firestorePort: 8080, functionsPort: 5001 };

bootstrapApplication(AppComponent, {
  providers: [
    // Angular 21 defaults to zoneless; NgZone uses and Nebular still rely on zone.js
    provideZoneChangeDetection(),
    MessageService,
    provideInjectionContextAngularFirestore(),
    providePrimeNgTheme(),
    provideCdkOverlaysBelowBodyPopups(),
    provideDialogEscapeGuard(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      const auth = getAuth();
      if (environment.e2e) {
        connectAuthEmulator(auth, `http://${emulator.host}:${emulator.authPort}`, {
          disableWarnings: true,
        });
      }
      return auth;
    }),
    // Create Auth (and connect its emulator) at bootstrap: compat Firestore otherwise starts it first,
    // a persisted user refreshes its token, and connectAuthEmulator then throws.
    provideEnvironmentInitializer(() => inject(Auth)),
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      AngularFireModule.initializeApp(environment.firebase),
      NbThemeModule.forRoot({ name: 'corporate' }),
      NbLayoutModule,
      NbEvaIconsModule,
      NbAuthModule.forRoot(),
      NbSidebarModule.forRoot(),
      NbMenuModule.forRoot(),
      NbDatepickerModule.forRoot(),
      NbDialogModule.forRoot(),
      NbWindowModule.forRoot(),
      ThemeModule.forRoot(),
      NgxDaterangepickerMd.forRoot(),
      NbToastrModule.forRoot(),
      MatDialogModule,
      NgxMatDatetimePickerModule,
      NgxMatNativeDateModule,
      MatNativeDateModule,
    ),
    {
      provide: USE_FIRESTORE_EMULATOR,
      // Separate from the functions flag on purpose: the `prod-emulator` build
      // runs callables locally against real production Firestore, so there is
      // no Firestore emulator to point at.
      useValue:
        environment.emulator && environment.emulateFirestore
          ? [emulator.host, emulator.firestorePort]
          : undefined,
    },
    {
      provide: USE_FUNCTIONS_EMULATOR,
      useValue: environment.emulator ? [emulator.host, emulator.functionsPort] : undefined,
    },
    {
      provide: USE_STORAGE_EMULATOR,
      // E2E only: its demo bucket does not exist, so uploads must stay on the E2E
      // storage emulator. The dev emulator setup keeps using the dev bucket.
      useValue: environment.e2e ? [emulator.host, emulator.storagePort] : undefined,
    },
    provideNgxWebstorage(withLocalStorage()),
    provideAnimations(),
    {
      provide: ENABLED_AUTH_PROVIDERS,
      useValue: [AuthProvider.MICROSOFT, AuthProvider.GOOGLE, AuthProvider.EMAIL],
    },
    // Enables ?token= sign-in (TokenProviderService) in the e2e build only.
    { provide: E2E_MODE, useValue: !!environment.e2e },
  ],
}).catch((err) => console.error('Bootstrap error', err));
